import { ERROR_TYPES, parseRawJson, toJson } from '@clevercloud/client/esm/utils/env-vars.js';
import { LitElement, css, html } from 'lit';
import { i18n } from '../../translations/translation.js';
import { CcEnvChangeEvent } from '../cc-env-var-form/cc-env-var-form.events.js';
import '../cc-input-text/cc-input-text.js';
import '../cc-link/cc-link.js';
import '../cc-notice/cc-notice.js';

/**
 * @type {Array<EnvVar>}
 */
const SKELETON_VARIABLES = [
  { name: 'VARIABLE_ONE', value: '' },
  { name: 'VARIABLE_FOOBAR', value: '' },
  { name: 'VARIABLE_PORT', value: '' },
];

/**
 * @import { EnvVarEditorState, EnvVarParseError, EnvVar, EnvVarRawError } from '../common.types.js'
 * @import { PropertyValues } from 'lit'
 */

/**
 * A high level environment variable editor to create/edit/delete all variables at once as a JSON text (properly parsed with validation and error messages).
 *
 * @cssdisplay block
 */
export class CcEnvVarEditorJson extends LitElement {
  static get properties() {
    return {
      disabled: { type: Boolean },
      readonly: { type: Boolean },
      state: { type: Object },
      _errors: { type: Array, state: true },
      _formattedErrors: { type: Array, state: true },
      _skeleton: { type: Boolean, state: true },
      _variablesAsJson: { type: String, state: true },
    };
  }

  constructor() {
    super();

    /** @type {boolean} Sets `disabled` attribute on inputs and buttons. */
    this.disabled = false;

    /** @type {boolean} Sets `readonly` attribute on main input and hides buttons. */
    this.readonly = false;

    /** @type {EnvVarEditorState} Sets the variables state. */
    this.state = { type: 'loading' };

    /** @type {EnvVarParseError[]} */
    this._errors = [];

    /** @type {boolean} */
    this._skeleton = false;

    /** @type {string} */
    this._variablesAsJson = '';
  }

  /** @param {EnvVarRawError[]} rawErrors */
  _setErrors(rawErrors) {
    this._errors = rawErrors.map(({ type, name }) => {
      if (type === ERROR_TYPES.INVALID_NAME) {
        return {
          msg: i18n('cc-env-var-editor-json.errors.invalid-name', { name }),
          isWarning: false,
        };
      }
      if (type === ERROR_TYPES.DUPLICATED_NAME) {
        return {
          msg: i18n('cc-env-var-editor-json.errors.duplicated-name', { name }),
          isWarning: false,
        };
      }
      if (type === ERROR_TYPES.INVALID_JSON) {
        return {
          msg: i18n('cc-env-var-editor-json.errors.invalid-json'),
          isWarning: false,
        };
      }
      if (type === ERROR_TYPES.INVALID_JSON_FORMAT) {
        return {
          msg: i18n('cc-env-var-editor-json.errors.invalid-json-format'),
          isWarning: false,
        };
      }
      if (type === ERROR_TYPES.INVALID_JSON_ENTRY) {
        return {
          msg: i18n('cc-env-var-editor-json.errors.invalid-json-entry'),
          isWarning: false,
        };
      }
      if (type === ERROR_TYPES.INVALID_NAME_STRICT) {
        return {
          msg: i18n('cc-env-var-editor-json.errors.invalid-name-strict', { name }),
          isWarning: false,
        };
      }
      if (type === ERROR_TYPES.JAVA_INFO) {
        return {
          msg: i18n('cc-env-var-editor-json.info.java-prop', { name }),
          isWarning: true,
        };
      }

      return { line: '?', msg: i18n('cc-env-var-editor-json.errors.unknown'), isWarning: false };
    });
  }

  /** @param {CcInputEvent} event */
  _onInput({ detail: value }) {
    if (this.state.type === 'loading') {
      return;
    }

    this._variablesAsJson = value;
    /** @type {{ variables: EnvVar[], errors: EnvVarRawError[] }} */
    const { variables, errors } = parseRawJson(value, { mode: this.state.validationMode });
    this._setErrors(errors);

    // for INVALID_JSON and INVALID_JSON_FORMAT errors, the parsed 'variables' is an empty array: we don't want to dispatch this case
    const hasJsonError = errors.some(
      ({ type }) => type === ERROR_TYPES.INVALID_JSON || type === ERROR_TYPES.INVALID_JSON_FORMAT,
    );
    if (!hasJsonError) {
      this.dispatchEvent(new CcEnvChangeEvent(variables));
    }
  }

  /** @param {PropertyValues<CcEnvVarEditorJson>} changedProperties */
  willUpdate(changedProperties) {
    if (changedProperties.has('state')) {
      this._skeleton = this.state.type === 'loading';
      const vars = this.state.type === 'loading' ? SKELETON_VARIABLES : this.state.variables;
      // Filter deleted variables and only keep name/value (strip internal state properties like isNew, isEdited, isDeleted)
      const filteredVariables = vars.filter(({ isDeleted }) => !isDeleted).map(({ name, value }) => ({ name, value }));
      this._variablesAsJson = toJson(filteredVariables);
      this._setErrors([]);
    }
  }

  render() {
    return html`
      ${!this.readonly ? html` <div class="example">${i18n('cc-env-var-editor-json.example')}</div> ` : ''}
      <cc-input-text
        label=${i18n('cc-env-var-editor-json.label')}
        hidden-label
        multi
        clipboard
        value=${this._variablesAsJson}
        ?disabled=${this.disabled}
        ?readonly=${this.readonly}
        ?skeleton=${this._skeleton}
        @cc-input=${this._onInput}
      ></cc-input-text>

      ${this._errors.length > 0
        ? html`
            <div class="error-list">
              ${this._errors.map(
                ({ msg, isWarning }) => html`
                  <cc-notice intent="${!isWarning ? 'warning' : 'info'}">
                    <div slot="message">${msg}</div>
                  </cc-notice>
                `,
              )}
            </div>
          `
        : ''}
    `;
  }

  static get styles() {
    return [
      // language=CSS
      css`
        :host {
          display: block;
        }

        :host([hidden]) {
          display: none;
        }

        .error-list {
          display: grid;
          grid-gap: 0.75em;
          margin-top: 1em;
        }

        .example {
          line-height: 1.5;
          padding-bottom: 1em;
        }

        /* i18n error message may contain <code> tags */

        cc-notice code,
        .example code {
          background-color: var(--cc-color-bg-neutral, #eee);
          border-radius: var(--cc-border-radius-default, 0.25em);
          font-family: var(--cc-ff-monospace, monospace);
          padding: 0.15em 0.3em;
        }

        cc-input-text {
          --cc-input-font-family: var(--cc-ff-monospace, monospace);
        }
      `,
    ];
  }
}

window.customElements.define('cc-env-var-editor-json', CcEnvVarEditorJson);
